import {
  collection,
  doc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  setDoc,
  getFirestore,
  CollectionReference,
  writeBatch,
  getCountFromServer,
  Timestamp,
  type DocumentData,
  type Transaction,
} from 'firebase/firestore';

import {
  ref,
  listAll,
  getStorage,
  getMetadata,
  deleteObject,
} from 'firebase/storage';

import {
  httpsCallable,
} from 'firebase/functions';

import {
  getFbFunctions,
} from './development';

import { getApp } from 'firebase/app';
import type { CustomType, Rack, Slot, SlotType, SlotTypeBucket, State, RackV2, SlotAssignment, SlotTypeFull } from './types';
import { errorMessage } from '../assets/js/helpers/FirebaseErrors';
import { planLimits as limits } from '../assets/js/helpers/BillingPlanHelper.ts';

function rackRef(context: State, backup = false): CollectionReference<DocumentData, DocumentData> | null {
  const fbApp = getApp(context.firebaseApp);
  const db = getFirestore(fbApp, context.dbid);

  if (context.team && context.userMode === 'team') {
    return collection(db, 'teams', context.team, backup ? 'rack-backups' : 'racks');
  } else if (context.user) {
    return collection(db, 'users', context.user.uid, backup ? 'rack-backups' : 'racks');
  } else {
    return null;
  }
}

function roomRef(context: State): CollectionReference<DocumentData, DocumentData> | null {
  const fbApp = getApp(context.firebaseApp);
  const db = getFirestore(fbApp, context.dbid);

  if (context.team && context.userMode === 'team') {
    return collection(db, 'teams', context.team, 'rooms');
  } else if (context.user) {
    return collection(db, 'users', context.user.uid, 'rooms');
  } else {
    return null;
  }
}

async function getMaxRacks(subscription: string) {
  switch (subscription.toLowerCase()) {
    case 'team':
    case 'plus':
      return limits.plus.racks();
    case 'basic':
      return limits.basic.racks();
    default:
      return limits.free.racks();
  }
}

export type RackTransferOperation = 'copy' | 'move';

export interface RackTransferDestination {
  type: 'user' | 'team';
  id: string;
}

interface RackTransferResult {
  success: boolean;
  operation: RackTransferOperation;
  rackId: string;
}

/**
 * Transfers a rack through a privileged callable so SSO-protected workspaces
 * can be the destination without switching the client's active workspace.
 */
export async function copyMoveRack(
  sourceRackId: string,
  operation: RackTransferOperation,
  destination: RackTransferDestination,
): Promise<RackTransferResult> {
  if (!this.user?.uid) {
    throw new Error('Authentication is required.');
  }

  const sourceWorkspaceType = this.userMode === 'team' ? 'team' : 'user';
  const sourceWorkspaceId = sourceWorkspaceType === 'team' ? this.team : this.user.uid;

  if (!sourceWorkspaceId) {
    throw new Error('Unable to determine the source workspace.');
  }

  try {
    const fbApp = getApp(this.firebaseApp);
    const functions = getFbFunctions(fbApp);
    const transfer = httpsCallable<{
      operation: RackTransferOperation;
      sourceWorkspaceType: 'user' | 'team';
      sourceWorkspaceId: string;
      destinationWorkspaceType: 'user' | 'team';
      destinationWorkspaceId: string;
      sourceRackId: string;
    }, RackTransferResult>(functions, 'racks-copyMoveRack');
    const response = await transfer({
      operation,
      sourceWorkspaceType,
      sourceWorkspaceId,
      destinationWorkspaceType: destination.type,
      destinationWorkspaceId: destination.id,
      sourceRackId,
    });

    return response.data;
  } catch (error: any) {
    console.error(error);
    // Callable HTTPS errors preserve the message supplied by the Firebase
    // function (for example, a missing permission or a rack-limit failure).
    // Do not pass those through the generic Firebase error mapper.
    const callableMessage = typeof error?.message === 'string' ? error.message.trim() : '';
    throw new Error(callableMessage || errorMessage(error));
  }
}

export async function addRack() {
  const version = import.meta.env.VITE_DEFAULT_RACK_VERSION || 'v1';

  if (this.teamData && this.teamData.role == 'viewer' && this.userMode == 'team') {
    throw new Error('You do not have permission to add racks.');
  }

  try {
    const localRackRef = rackRef(this);

    if (!localRackRef) {
      throw new Error('Error connecting to database.');
    }

    const defaultRack = {
      name: 'New Rack',
      location: '',
      tags: [],
      size: 45,
      depth: 29,
      maxPowerBudget: null,
      verticalChannelsLeft: 0,
      verticalChannelsRight: 0,
      slots: [],
      notes: '',
      style: 'standard',
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date()),
    };

    if (version === 'v2') {
      delete (defaultRack as any).slots;
      (defaultRack as unknown as RackV2).slotAssignments = [];
      (defaultRack as unknown as RackV2).version = 'v2';
    }

    const newDocId = doc(localRackRef).id;

    const docRef = await this.runSubscriptionTransaction(async (sub: 'Free' | 'Basic' | 'Plus', tx: Transaction) => {
      const maxRacks = await getMaxRacks(sub);
      let numRacks = 0;

      try {
        const snapshot = await getCountFromServer(localRackRef);
        numRacks = snapshot.data().count;
      } catch {
        // User has no racks
      }

      if (numRacks < maxRacks) {
        tx.set(doc(localRackRef, newDocId), defaultRack);
        return Promise.resolve(newDocId);
      } else {
        return Promise.resolve(false);
      }
    });

    if (docRef) {
      return docRef;
    } else {
      throw new Error('You have reached the maximum number of racks.');
    }

  } catch (e: any) {
    console.error(e);
    if (e.code === 'permission-denied' || e.message.includes('maximum number of racks')) {
      throw new Error('You have reached the maximum number of racks.');
    } else {
      throw new Error(errorMessage(e));
    }
  }
}

export async function updateRack(rack: Rack | RackV2) {
  if (this.teamData && this.teamData.role == 'viewer' && this.userMode == 'team') {
    throw new Error('You do not have permission to update racks.');
  }

  try {
    const localRackRef = rackRef(this);

    if (!localRackRef) {
      throw new Error('Error retrieving rack from database.');
    }

    rack.updatedAt = Timestamp.fromDate(new Date());
    const curDoc = doc(localRackRef, rack.id);
    await setDoc(curDoc, rack);
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

export async function deleteRack(id: string) {
  if (this.teamData && this.teamData.role == 'viewer' && this.userMode == 'team') {
    throw new Error('You do not have permission to delete racks.');
  }

  try {
    const localRackRef = rackRef(this);

    if (!localRackRef) {
      throw new Error('Error retrieving rack from database.');
    }

    const curDoc = doc(localRackRef, id);
    await deleteDoc(curDoc);
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

const calculateSlotSpace = (slot: Slot | SlotAssignment) => slot.halfWidth ? slot.size / 2 : slot.size;

export async function duplicateRack(id: string) {
  if (this.teamData && this.teamData.role == 'viewer' && this.userMode == 'team') {
    throw new Error('You do not have permission to duplicate racks.');
  }

  try {
    // Fetch the existing rack
    const existingRack = await this.fetchRack({ id, withRooms: false });

    if (!existingRack) {
      throw new Error('Rack not found.');
    }

    const localRackRef = rackRef(this);

    if (!localRackRef) {
      throw new Error('Error connecting to database.');
    }

    // Create a copy of the rack data
    const duplicatedRack: RackV2 = {
      ...existingRack,
      rooms: [], // Don't duplicate rooms - they should be added separately
      name: 'Copy of ' + (existingRack.name || 'New Rack'),
      createdAt: Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date()),
    };

    // Remove the id so a new one will be generated
    delete duplicatedRack.id;

    const newDocId = doc(localRackRef).id;

    // Deep copy slots or slotAssignments
    if (duplicatedRack.version === 'v2' && duplicatedRack.slotAssignments) {
      duplicatedRack.slotAssignments = duplicatedRack.slotAssignments.map((assignment: SlotAssignment) => ({
        ...assignment,
        assignedAt: Timestamp.fromDate(new Date()),
      }));
    } else if (duplicatedRack.slots) {
      duplicatedRack.slots = duplicatedRack.slots.map((slot: Slot) => ({
        ...slot,
        id: crypto.randomUUID(),
      }));
    }

    const docRef = await this.runSubscriptionTransaction(async (sub: 'Free' | 'Basic' | 'Plus', tx: Transaction) => {
      const maxRacks = await getMaxRacks(sub);
      let numRacks = 0;

      try {
        const snapshot = await getCountFromServer(localRackRef);
        numRacks = snapshot.data().count;
      } catch {
        // User has no racks
      }

      if (numRacks < maxRacks) {
        tx.set(doc(localRackRef, newDocId), duplicatedRack);

        if (duplicatedRack.version === 'v2' && duplicatedRack.slotAssignments) {
          // For each slot assignment, update the slot as well
          duplicatedRack.slotAssignments.forEach((assignment: SlotAssignment) => {
            this.assignSlotToRack(assignment.id, newDocId, assignment.slot, tx);
          });
        }
        return Promise.resolve(newDocId);
      } else {
        return Promise.resolve(false);
      }
    });

    if (docRef) {
      return docRef;
    } else {
      throw new Error('You have reached the maximum number of racks.');
    }

  } catch (e: any) {
    console.error(e);
    if (e.code === 'permission-denied') {
      throw new Error('You have reached the maximum number of racks.');
    } else {
      throw new Error(errorMessage(e));
    }
  }
}

export async function fetchRacks(): Promise<{ racks: Rack[], count: number }> {
  try {
    const rackData = {
      racks: [] as Rack[],
      count: 0,
    };

    const localRackRef = rackRef(this);
    const localRoomRef = roomRef(this);

    if (!localRackRef || !localRoomRef) {
      return rackData;
    }

    const querySnapshot = await getDocs(localRackRef);
    const roomSnapshot = await getDocs(localRoomRef);

    const rooms = roomSnapshot.docs.map((doc) => {
      return {
        id: doc.id,
        rackIds: doc.data().rackIds || [],
        name: doc.data().name,
      };
    });

    querySnapshot.forEach((doc) => {
      const data = doc.data() as Rack;
      data.id = doc.id;
      let slotsFull = 0;
      if (data.slots) {
        data.slots.forEach((slot: Slot) => {
          slotsFull += calculateSlotSpace(slot);
        });
      }
      if (slotsFull == 0) {
        data.full = '0%';
      } else {
        data.full = Math.round((slotsFull / data.size) * 100) + '%';
      }
      data.slotsFull = slotsFull;

      const roomsList = [] as { id: string, name: string, rackIds: string[] }[];
      // If the rack is in a room, add it to the list
      rooms.find((room) => {
        if (room.rackIds.includes(doc.id)) {
          roomsList.push(room);
        }
      });

      data.rooms = roomsList;

      rackData.racks.push(data);
      rackData.count++;
    });

    return rackData;
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

export async function fetchRacksMinimal(): Promise<{ racks: { id: string, name: string }[], count: number }> {
  try {
    const rackData = {
      racks: [] as { id: string, name: string }[],
      count: 0,
    };

    const localRackRef = rackRef(this);

    if (!localRackRef) {
      return rackData;
    }

    const querySnapshot = await getDocs(localRackRef);

    querySnapshot.forEach((doc) => {
      const data = doc.data() as { id: string, name: string };
      data.id = doc.id;

      rackData.racks.push(data);
      rackData.count++;
    });

    return rackData;
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

/**
 * Migrates a rack from v1 to v2, converting embedded slots to slot assignments
 */
export async function migrateRackToV2(rackId: string): Promise<void> {
  try {
    const localRackRef = rackRef(this);
    if (!localRackRef) {
      throw new Error('Error retrieving rack from database.');
    }

    const rackDocRef = doc(localRackRef, rackId);
    const rackDoc = await getDoc(rackDocRef);

    if (!rackDoc.exists()) {
      throw new Error('Rack not found');
    }

    const rackData = rackDoc.data() as any;

    if (rackData.version === 'v2') {
      return;
    }

    // Backup the v1 rack data
    const backupRackData = {
      ...rackData,
      version: 'v1',
    };
    const backupRackRef = rackRef(this, true);

    if (!backupRackRef) {
      throw new Error('Error creating backup.');
    }

    await setDoc(doc(backupRackRef, rackId), backupRackData);

    // Get the database instance for batch operations
    const fbApp = getApp(this.firebaseApp);
    const db = getFirestore(fbApp, this.dbid);
    const batch = writeBatch(db);

    // Create slots collection reference
    let slotsColRef: CollectionReference;
    if (this.team && this.userMode === 'team') {
      slotsColRef = collection(db, 'teams', this.team, 'slots');
    } else if (this.user) {
      slotsColRef = collection(db, 'users', this.user.uid, 'slots');
    } else {
      throw new Error('No user or team context available');
    }

    // Step 1: Create new slot objects in the centralized collection
    const newIds: string[] = [];
    const slotAssignments: SlotAssignment[] = [];

    // Validate that all slots have required data
    for (const slot of rackData.slots) {
      if (!slot.type || !slot.size) {
        throw new Error(`Slot ${slot.id} is missing required type or size data`);
      }
    }

    for (const slot of rackData.slots) {
      // Create new slot document
      const newSlotDocRef = doc(slotsColRef);
      newIds.push(newSlotDocRef.id);
      const now = new Date();

      // Prepare slot data for the new document
      const newSlotData = {
        name: slot.name || null,
        serial: slot.serial || null,
        model: slot.model || null,
        size: slot.size,
        type: slot.type,
        notes: slot.notes || null,
        bodyColor: slot.bodyColor ?? null,
        rearType: slot.rearType ?? null,
        depth: slot.depth ?? null,
        depthUnit: slot.depthUnit || 'inches',
        powerConsumption: slot.powerConsumption ?? null,
        weight: slot.weight ?? null,
        weightUnit: slot.weightUnit || 'pounds',
        tags: slot.tags || null,
        createdBy: this.team || this.user?.uid || 'unknown',
        isInventory: true,
        rackAssignments: [{
          rackId: rackId,
          slot: slot.slot,
          channel: slot.channel || 1,
          assignedAt: now,
          assignedBy: this.user?.uid || 'unknown',
        }],
        createdAt: now,
        updatedAt: now,
      };

      // Add slot creation to batch
      batch.set(newSlotDocRef, newSlotData);

      // Create slot assignment for the rack
      slotAssignments.push({
        id: newSlotDocRef.id,
        slot: slot.slot || 0,
        assignedAt: Timestamp.fromDate(now),
        assignedBy: this.user?.uid || 'unknown',
        type: slot.type,
        size: slot.size,
        name: slot.name || '',
        side: slot.side || 'left',
        channel: slot.channel || 1,
        orientation: slot.orientation || 'horizontal',
        verticalHeight: slot.verticalHeight || 0,
        verticalHeightUnit: slot.verticalHeightUnit || 'inches',
        bodyColor: slot.bodyColor ?? null,
        rearType: slot.rearType ?? null,
        depth: slot.depth ?? null,
        depthUnit: slot.depthUnit || 'inches',
        powerConsumption: slot.powerConsumption ?? null,
        weight: slot.weight ?? null,
        weightUnit: slot.weightUnit || 'pounds',
      });
    }

    // Step 2: Update rack to v2 with new slot assignments
    batch.update(rackDocRef, {
      version: 'v2',
      slotAssignments: slotAssignments,
      slots: [], // Remove old slots
      verticalChannelsLeft: rackData.verticalChannelsLeft ?? 0,
      verticalChannelsRight: rackData.verticalChannelsRight ?? 0,
    });

    // Step 3: Execute all operations in a single batch
    await batch.commit();

    console.log(`Successfully migrated rack ${rackId} to v2 with ${newIds.length} slots`);
    console.log('New slot IDs:', newIds);
    console.log('Slot assignments:', slotAssignments);
  } catch (e: any) {
    console.error('Error migrating rack to v2:', e);

    // Provide more specific error messages
    if (e.message.includes('permission')) {
      throw new Error('Permission denied: You do not have access to migrate this rack');
    } else if (e.message.includes('not found')) {
      throw new Error('Rack not found or has been deleted');
    } else if (e.message.includes('missing required')) {
      throw new Error(`Migration failed: ${e.message}`);
    } else {
      throw new Error(`Failed to migrate rack to v2: ${e.message || 'Unknown error'}`);
    }
  }
}

export async function fetchRack({ id, withRooms = true }: { id: string, withRooms?: boolean }): Promise<RackV2> {
  try {
    const localRackRef = rackRef(this);

    if (!localRackRef) {
      throw new Error('Error retrieving rack from database.');
    }

    const docRef = doc(localRackRef, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      data.id = docSnap.id;

      if (withRooms) {
        data.rooms = await this.getRackRooms(id);
      }
      delete data.room;

      const rackData = data as RackV2;
      const v2 = rackData.version === 'v2';
      rackData.version = v2 ? 'v2' : 'v1';
      const targetSlots = v2 ? rackData.slotAssignments : rackData.slots;

      if (v2) {
        rackData.slots = null;
      } else {
        rackData.slotAssignments = null;
      }

      let slotsFull = 0;
      if (targetSlots) {
        targetSlots.forEach((slot: Slot | SlotAssignment) => {
          slotsFull += calculateSlotSpace(slot);
        });
      }
      rackData.slotsFull = slotsFull;
      rackData.full = slotsFull === 0 ? '0%' : Math.round((slotsFull / rackData.size) * 100) + '%';
      return rackData;
    } else {
      throw new Error('Document doesn\'t exist');
    }
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

export async function getRackRooms(id: string) {
  const localRoomRef = roomRef(this);

  if (!localRoomRef) {
    throw new Error('Error retrieving room from database.');
  } else {
    const q = query(localRoomRef, where('rackIds', 'array-contains', id));
    const querySnapshot = await getDocs(q);
    const rooms = [] as { id: string, name: string }[];
    querySnapshot.forEach((doc) => {
      rooms.push({
        id: doc.id,
        name: doc.data().name,
      });
    });

    return rooms;
  }
}

export async function getRackCount() {
  try {
    const localRackRef = rackRef(this);

    if (!localRackRef) {
      return 0;
    }
    const snapshot = await getCountFromServer(localRackRef);
    return snapshot.data().count;
  } catch (e: any) {
    console.warn(e);
    return 0;
  }
}

export async function fetchGlobalTypes(): Promise<SlotTypeBucket> {
  const fbApp = getApp(this.firebaseApp);
  const db = getFirestore(fbApp, this.dbid);
  const storage = getStorage(fbApp);

  const globalTypesRef = collection(db, 'slottypes');
  const globalRef = ref(storage, 'global/images/types');

  let slotSchemaIdx = 0;
  let hasMore = true;

  const types = [];

  while (hasMore) {
    const querySnapshot = await getDoc(doc(globalTypesRef, slotSchemaIdx.toString()));
    if (querySnapshot.exists()) {
      const snapData = querySnapshot.data();
      types.push(...snapData.types);
      hasMore = snapData.hasMore;
    } else {
      hasMore = false;
    }
    slotSchemaIdx += 1;
  }

  const typeValue = (key: string) => {
    if (key === 'global-blank') return 'blank';
    if (key === 'global-half-width-blank') return 'half-width-blank';
    if (key === 'global-vertical-blank') return 'vertical-blank';
    return key.split('global/images/types/')[1] ?? key;
  };

  const typeFullPath = (key: string) => {
    if (
      key === 'global-blank' || key === 'blank' ||
      key === 'global-half-width-blank' || key === 'half-width-blank' ||
      key === 'global-vertical-blank' || key === 'vertical-blank'
    ) {
      return null;
    }
    return key;
  };

  const options = types.flatMap((type) => {
    if (type.metadata?.label && type.metadata?.slotSize) {
      return {
        label: type.metadata.label,
        title: type.metadata.label,
        value: typeValue(type.value),
        size: parseInt(type.metadata.slotSize),
        key: type.key,
        metadata: type.metadata,
        uploadedBy: type.metadata?.uploadedBy ?? null,
        team: type.metadata?.team ?? null,
        fullPath: typeFullPath(type.value),
        manufacturer: type.metadata?.manufacturer ?? null,
        category: type.metadata?.category ?? null,
        bodyColor: type.metadata?.bodyColor ?? null,
        rearType: type.metadata?.rearType ?? null,
        powerConsumption: type.metadata?.powerConsumption ?? null,
        depth: type.metadata?.depth ?? null,
        depthUnit: type.metadata?.depthUnit ?? null,
        weight: type.metadata?.weight ?? null,
        weightUnit: type.metadata?.weightUnit ?? null,
        halfWidth: type.metadata?.halfWidth ?? null,
        isPublic: type.metadata?.isPublic ?? null,
        moderationStatus: type.metadata?.moderationStatus ?? null,
        timeCreated: type.timeCreated,
        orientation: type.metadata?.orientation ?? null,
        verticalHeight: type.metadata?.verticalHeight ?? null,
        verticalHeightUnit: type.metadata?.verticalHeightUnit ?? null,
      };
    }
    return null; // Return null if metadata is missing or invalid
  }).filter((type) => type !== null);

  return { label: 'Public', ref: globalRef, source: 'global', options };
}

export async function fetchLocalTypes(returnMetadata = false) {
  const fbApp = getApp(this.firebaseApp);
  const storage = getStorage(fbApp);

  const userRef = ref(storage, `user/${this.user.uid}/images/types`);
  const teamRef = this.team && this.userMode === 'team'
    ? ref(storage, `teams/${this.team}/images/types`)
    : null;

  let bucket = { label: 'My Private Types', ref: userRef, source: 'user', options: [] } as any;

  if (teamRef && this.userMode === 'team') {
    bucket = { label: 'Team Private Types', ref: teamRef, source: 'team', options: [] };
  }

  if (bucket.ref) {
    const res = await listAll(bucket.ref);

    const bucketTypes = await Promise.all(
      res.items.map(async (itemRef, index) => {
        try {
          const metadata = await getMetadata(itemRef);

          if (metadata?.customMetadata?.label && metadata?.customMetadata?.slotSize) {
            return {
              label: metadata.customMetadata.label,
              title: metadata.customMetadata.label,
              value: itemRef.name,
              size: parseInt(metadata.customMetadata.slotSize),
              key: `${bucket.source}-${index}`,
              metadata: returnMetadata ? metadata : null,
              uploadedBy: metadata?.customMetadata?.uploadedBy ?? null,
              team: metadata?.customMetadata?.team ?? null,
              fullPath: itemRef.fullPath,
              manufacturer: metadata?.customMetadata?.manufacturer ?? null,
              category: metadata?.customMetadata?.category ?? null,
              bodyColor: metadata?.customMetadata?.bodyColor ?? null,
              rearType: metadata?.customMetadata?.rearType ?? null,
              powerConsumption: metadata?.customMetadata?.powerConsumption ?? null,
              depth: metadata?.customMetadata?.depth ?? null,
              depthUnit: metadata?.customMetadata?.depthUnit ?? null,
              weight: metadata?.customMetadata?.weight ?? null,
              weightUnit: metadata?.customMetadata?.weightUnit ?? null,
              halfWidth: metadata?.customMetadata?.halfWidth ?? null,
              isPublic: metadata?.customMetadata?.isPublic ?? null,
              moderationStatus: metadata?.customMetadata?.moderationStatus ?? null,
              timeCreated: metadata.timeCreated,
              orientation: metadata?.customMetadata?.orientation ?? null,
              verticalHeight: metadata?.customMetadata?.verticalHeight ?? null,
              verticalHeightUnit: metadata?.customMetadata?.verticalHeightUnit ?? null,
            };
          }
          return null; // Return null if metadata is missing or invalid
        } catch (error) {
          const rackName = itemRef.name;
          console.error(`Error fetching metadata for item: ${rackName}`, error);
          return null; // Return null for errors
        }
      }),
    );

    // Sort the bucket's types by label
    bucketTypes.sort((a, b) => {
      if (a === null) return 1;
      if (b === null) return -1;

      return a.label.localeCompare(b.label);
    });

    // Add valid types to the bucket's types array
    bucket.options.push(
      ...bucketTypes.filter((type) => type !== null) as SlotTypeFull[],
    );
  }

  return bucket;
}

export async function fetchTypes(returnMetadata = false) {
  const fbApp = getApp(this.firebaseApp);
  const storage = getStorage(fbApp);

  if (!this.user) {
    return [];
  }

  try {
    const globalRef = ref(storage, 'global/images/types');

    const bucketPromises = Promise.all([
      this.fetchGlobalTypes(),
      this.fetchLocalTypes(returnMetadata),
    ]);

    const [globalBucket, localBucket] = await bucketPromises;

    const buckets = [globalBucket, localBucket] as SlotTypeBucket[];

    // Local Public Buckets
    const userLocalPublic = { label: 'My Published Types', ref: globalRef, source: 'global', options: [] } as SlotTypeBucket;
    const teamLocalPublic = { label: 'Team Published Types', ref: globalRef, source: 'team', options: [] } as SlotTypeBucket;

    // Iterate over the global bucket and pull items with uploadedBy or team matching the current user/team
    globalBucket.options = globalBucket.options.filter((type: SlotType) => {
      if (type.uploadedBy === this.user.uid) {
        userLocalPublic.options.push(type);
        return false;
      } else if (this.team !== null && type.team === this.team) {
        teamLocalPublic.options.push(type);
        return false;
      }
      return true;
    });

    // Sort each bucket by label
    userLocalPublic.options.sort((a, b) => {
      if (a.label === 'Blank') return -1;
      if (b.label === 'Blank') return 1;
      return a.label.localeCompare(b.label);
    });
    teamLocalPublic.options.sort((a, b) => a?.label.localeCompare(b?.label));

    // Add local public buckets to the buckets array
    buckets.push(userLocalPublic);
    buckets.push(teamLocalPublic);

    // Filter out buckets with no options
    const filteredBuckets = buckets.filter((bucket) => bucket.options.length > 0);

    // Sort buckets public -> local public -> private
    filteredBuckets.sort((a, b) => {
      if (a.label === 'Public') return -1;
      if (b.label === 'Public') return 1;
      if (a.label === 'My Published Types') return -1;
      if (b.label === 'My Published Types') return 1;
      if (a.label === 'Team Published Types') return -1;
      if (b.label === 'Team Published Types') return 1;
      return 0;
    });

    return filteredBuckets;
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

export async function fetchTypeManufacturersAndCategories() {
  const fbApp = getApp(this.firebaseApp);
  const storage = getStorage(fbApp);

  if (!this.user) {
    return {
      manufacturers: [],
      categories: [],
    };
  }

  try {
    const userRef = ref(storage, `user/${this.user.uid}/images/types`);
    const teamRef = this.team && this.userMode === 'team'
      ? ref(storage, `teams/${this.team}/images/types`)
      : null;

    const manufacturers = new Set(['Generic']);
    const categories = new Set(['General']);
    const buckets = [];

    if (teamRef && this.userMode === 'team') {
      buckets.push(teamRef);
    } else {
      buckets.push(userRef);
    }

    // Fetch types for each bucket
    for (const bucket of buckets) {
      if (bucket) {
        const res = await listAll(bucket);

        await Promise.all(
          res.items.map(async (itemRef) => {
            try {
              const metadata = await getMetadata(itemRef);

              if (metadata?.customMetadata?.manufacturer) {
                manufacturers.add(metadata.customMetadata.manufacturer);
              }

              if (metadata?.customMetadata?.category) {
                categories.add(metadata.customMetadata.category);
              }
            } catch (error) {
              console.error(`Error fetching metadata for item: ${itemRef.name}`, error);
            }
          }),
        );
      }
    }

    const globalTypes = await this.fetchGlobalTypes();
    globalTypes.options.forEach((type: SlotType) => {
      if (type.manufacturer) {
        manufacturers.add(type.manufacturer);
      }
      if (type.category) {
        categories.add(type.category);
      }
    });

    return {
      manufacturers: Array.from(manufacturers).sort((a, b) => a.localeCompare(b)).map((manufacturer) => {
        return { value: manufacturer, text: manufacturer };
      }),
      categories: Array.from(categories).sort((a, b) => a.localeCompare(b)).map((category) => {
        return { value: category, text: category };
      }),
    };
  } catch {
    return {
      manufacturers: [],
      categories: [],
    };
  }
}

export async function uploadType(data: CustomType) {
  // Check if the user is read-only
  if (this.teamData && this.teamData.role == 'viewer' && this.userMode == 'team') {
    throw new Error('You do not have permission to add types.');
  }

  const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
  const isAllowedType = allowedTypes.includes(data.image.type);
  if (!isAllowedType) {
    throw new Error('Image must be one of the following types: JPEG, PNG, WEBP, AVIF!');
  }
  const isLt1M = data.image.size / 1024 / 1024 < 1;
  if (!isLt1M) {
    throw new Error('Image must be smaller than 1MB!');
  }

  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);

  const uploadTypeFn = httpsCallable(functions, 'files-uploadType');

  try {
    const result = await uploadTypeFn({
      team: this.team && this.userMode === 'team' ? this.team : null,
      imageName: data.image.name,
      imageData: data.image.data,
      label: data.label,
      slotSize: data.size,
      isPublic: data.publish,
      contentType: data.image.type,
      moderator: data.moderator,
      manufacturer: data.manufacturer,
      category: data.category,
      bodyColor: data.bodyColor,
      rearType: data.rearType,
      powerConsumption: data.powerConsumption,
      depth: data.depth,
      depthUnit: data.depthUnit,
      weight: data.weight,
      weightUnit: data.weightUnit,
      halfWidth: data.halfWidth,
      orientation: data.orientation,
      verticalHeight: data.verticalHeight,
      verticalHeightUnit: data.verticalHeightUnit,
    });
    return result.data;
  } catch (e: any) {
    throw new Error(e);
  }
}

export async function fetchTypeName(slot: string): Promise<string> {
  const fbApp = getApp(this.firebaseApp);
  const storage = getStorage(fbApp);

  try {
    // Attempt to lookup file in global folder, then team (if in team mode), then user
    const globalRef = ref(storage, 'global/images/types/' + slot);

    const metadata = await getMetadata(globalRef);

    if (metadata?.customMetadata?.label) {
      return metadata.customMetadata.label;
    } else {
      return '';
    }
  } catch {
    try {
      let curRef;

      if (!this.user) {
        return '';
      }

      if (this.team && this.userMode === 'team') {
        curRef = ref(storage, 'teams/' + this.team + '/images/types/' + slot);
      } else {
        curRef = ref(storage, 'user/' + this.user.uid + '/images/types/' + slot);
      }

      const metadata = await getMetadata(curRef);
      if (metadata?.customMetadata?.label) {
        return metadata.customMetadata.label;
      } else {
        return '';
      }
    } catch (e) {
      console.error(e);
      return 'Invalid Type';
    }
  }
}

export async function getPrivateSlotCount() {
  const fbApp = getApp(this.firebaseApp);
  const storage = getStorage(fbApp);

  if (!this.user) {
    return 0;
  }

  try {
    if (this.team && this.userMode === 'team') {
      const teamRef = ref(storage, `teams/${this.team}/images/types`);
      const res = await listAll(teamRef);
      return res.items.length;
    } else {
      const userRef = ref(storage, `user/${this.user.uid}/images/types`);
      const res = await listAll(userRef);
      return res.items.length;
    }
  } catch (e: any) {
    console.warn(e);
    return 0;
  }
}

export async function getPublicSlots(): Promise<SlotTypeFull[]> {
  const fbApp = getApp(this.firebaseApp);
  const storage = getStorage(fbApp);

  if (!this.user) {
    return [] as SlotTypeFull[];
  }

  let res;

  try {
    if (this.team && this.userMode === 'team') {
      const teamRef = ref(storage, `global/images/moderation_queue/team/${this.team}/types`);
      res = await listAll(teamRef);

    } else {
      const userRef = ref(storage, `global/images/moderation_queue/user/${this.user.uid}/types`);
      res = await listAll(userRef);
    }

    const publicTypes = await Promise.all(
      res.items.map(async (itemRef, index) => {

        try {
          const metadata = await getMetadata(itemRef);

          if (metadata?.customMetadata?.label && metadata?.customMetadata?.slotSize) {
            return {
              label: metadata.customMetadata.label,
              title: metadata.customMetadata.label,
              value: itemRef.name,
              size: parseInt(metadata.customMetadata.slotSize),
              key: `${this.userMode}-${index}`,
              metadata: metadata,
              uploadedBy: metadata?.customMetadata?.uploadedBy,
              team: metadata?.customMetadata?.team,
              fullPath: metadata.fullPath,
              manufacturer: metadata?.customMetadata?.manufacturer ?? null,
              category: metadata?.customMetadata?.category ?? null,
              bodyColor: metadata?.customMetadata?.bodyColor ?? null,
              rearType: metadata?.customMetadata?.rearType ?? null,
              powerConsumption: metadata?.customMetadata?.powerConsumption ?? null,
              depth: metadata?.customMetadata?.depth ?? null,
              depthUnit: metadata?.customMetadata?.depthUnit ?? null,
              weight: metadata?.customMetadata?.weight ?? null,
              weightUnit: metadata?.customMetadata?.weightUnit ?? null,
              halfWidth: metadata?.customMetadata?.halfWidth ?? null,
              isPublic: metadata?.customMetadata?.isPublic ?? null,
              moderationStatus: metadata?.customMetadata?.moderationStatus ?? null,
              timeCreated: metadata.timeCreated,
              orientation: metadata?.customMetadata?.orientation ?? null,
              verticalHeight: metadata?.customMetadata?.verticalHeight ?? null,
              verticalHeightUnit: metadata?.customMetadata?.verticalHeightUnit ?? null,
            } as SlotTypeFull;
          }
        } catch (error) {
          const rackName = itemRef.name;
          console.error(`Error fetching metadata for item: ${rackName}`, error);
        }
      }),
    );

    return publicTypes.filter((item) => item !== undefined);
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

export async function deleteSlotType(filePath: string) {
  const fbApp = getApp(this.firebaseApp);
  const storage = getStorage(fbApp);

  // Check if the user is read-only
  if (this.teamData && this.teamData.role == 'viewer' && this.userMode == 'team') {
    throw new Error('You do not have permission to delete slots.');
  }

  try {
    await deleteObject(ref(storage, filePath));
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

export async function deletePendingPublish(value: string) {
  const fbApp = getApp(this.firebaseApp);
  const storage = getStorage(fbApp);

  if (!this.user) {
    return;
  }

  let rootPath;

  if (this.team && this.userMode === 'team') {
    rootPath = `global/images/moderation_queue/team/${this.team}/types`;
  } else {
    rootPath = `global/images/moderation_queue/user/${this.user.uid}/types`;
  }

  const filePath = `${rootPath}/${value}`;

  try {
    await deleteObject(ref(storage, filePath));
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

// Get an associative array with the number of times each slot type has been used by the current user or team
export async function getSlotUsageCounts() {
  const fbApp = getApp(this.firebaseApp);

  if (!this.user) {
    return {};
  }

  let rootPath;

  if (this.team && this.userMode === 'team') {
    rootPath = `teams/${this.team}/racks`;
  } else {
    rootPath = `users/${this.user.uid}/racks`;
  }

  // If path does not exist, return empty array
  const db = getFirestore(fbApp, this.dbid);
  const racksRef = collection(db, rootPath);

  // Query for docs where slots is not empty
  const v1Query = query(racksRef, where('slots', '!=', []));
  const v2Query = query(racksRef, where('slotAssignments', '!=', []));
  const v1QuerySnapshot = await getDocs(v1Query);
  const v2QuerySnapshot = await getDocs(v2Query);

  const querySnapshot = [...new Map([...v1QuerySnapshot.docs, ...v2QuerySnapshot.docs]
    .map(doc => [doc.id, doc])).values()];

  const slotCounts = {} as Record<string, { count: number, racks: { id: string, name: string, count: number }[] }>;

  const incrementUsage = (rackId: string, rackName: string, slotType: string) => {
    const existing = slotCounts[slotType] ?? { count: 0, racks: [] };
    const rackIndex = existing.racks.findIndex((rack) => rack.id === rackId);

    if (rackIndex === -1) {
      existing.racks.push({ id: rackId, name: rackName, count: 1 });
    } else {
      existing.racks[rackIndex].count += 1;
    }

    existing.count += 1;
    slotCounts[slotType] = existing;
  };

  querySnapshot.forEach((doc) => {
    const data = doc.data();
    const rackName = data.name ?? 'Unnamed Rack';

    if (data.slotAssignments) {
      data.slotAssignments.forEach((slot: SlotAssignment) => {
        incrementUsage(doc.id, rackName, slot.type);
      });
    } else if (data.slots) {
      data.slots.forEach((slot: Slot) => {
        incrementUsage(doc.id, rackName, slot.type);
      });
    }
  });

  return slotCounts;
}

export async function publishPrivateSlotType(value: string) {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  const publishType = httpsCallable(functions, 'files-publishPrivateType');

  try {
    const result = await publishType({
      team: this.team && this.userMode === 'team' ? this.team : null,
      imageName: value,
    });
    return result.data;
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}

export async function resetSlotTypeStatus(value: string) {
  const fbApp = getApp(this.firebaseApp);
  const functions = getFbFunctions(fbApp);
  const resetType = httpsCallable(functions, 'files-resetFileStatus');

  try {
    const result = await resetType({
      team: this.team && this.userMode === 'team' ? this.team : null,
      imageName: value,
    });
    return result.data as { success: boolean, message: string };
  } catch (e: any) {
    console.error(e);
    throw new Error(errorMessage(e));
  }
}
